import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const CruiseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  
  const [formData, setFormData] = useState({
    // Basic Info
    title: '',
    subtitle: '',
    slug: '',
    status: 'active',
    image_url: '',
    gallery_images: [],
    
    // Dates
    departure_date: '',
    duration_days: '',
    duration_nights: '',
    deadline_date: '',
    
    // Cruise Info
    ship_name: '',
    company_name: '',
    route: '',
    price_from: '',
    
    // Cabin Categories
    cabin_categories: [
      { type: 'внутренняя', price: '' },
      { type: 'внешняя', price: '' },
      { type: 'с балконом', price: '' },
      { type: 'сьют', price: '' }
    ],
    
    // Ports Schedule
    ports_schedule: [
      { port_number: 1, port_name: '', date: '', arrival: '-', stay: '-', departure: '', notes: '' }
    ],
    
    // Description
    description: '',
    included: [''],
    excluded: [''],
    comment: '',
    highlights: [''],
    
    // Flight Info
    flight_info: '',
    flight_departure: '',
    flight_return: '',
    
    // Booking Conditions
    booking_conditions: '',
    deposit_amount: '',
    final_payment_days: '',
    visa_info: '',
    
    // Daily Program
    daily_program: [
      { day_number: 1, day_title: '', day_date: '', day_description: '' }
    ],
    
    // SEO
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });

  // Section definitions
  const sections = [
    { id: 'basic', label: 'Основная информация', icon: '📋' },
    { id: 'dates', label: 'Даты и длительность', icon: '📅' },
    { id: 'cruise', label: 'Информация о круизе', icon: '🚢' },
    { id: 'cabins', label: 'Категории кабин', icon: '🛏️' },
    { id: 'ports', label: 'Расписание портов', icon: '⚓' },
    { id: 'description', label: 'Описание и включено', icon: '📝' },
    { id: 'flight', label: 'Информация о перелете', icon: '✈️' },
    { id: 'booking', label: 'Условия бронирования', icon: '💳' },
    { id: 'program', label: 'Программа по дням', icon: '🗓️' },
    { id: 'seo', label: 'SEO', icon: '🔍' }
  ];

  const fetchCruise = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cruises')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      if (data) {
        setFormData({
          title: data.title || '',
          subtitle: data.subtitle || '',
          slug: data.slug || '',
          status: data.status || 'active',
          image_url: data.image_url || '',
          gallery_images: data.gallery_images || [],
          departure_date: data.departure_date || '',
          duration_days: data.duration_days || '',
          duration_nights: data.duration_nights || '',
          deadline_date: data.deadline_date || '',
          ship_name: data.ship_name || '',
          company_name: data.company_name || '',
          route: data.route || '',
          price_from: data.price_from || '',
          cabin_categories: data.cabin_categories || [],
          ports_schedule: data.ports_schedule || [],
          description: data.description || '',
          included: data.included || [''],
          excluded: data.excluded || [''],
          comment: data.comment || '',
          highlights: data.highlights || [''],
          flight_info: data.flight_info || '',
          flight_departure: data.flight_departure || '',
          flight_return: data.flight_return || '',
          booking_conditions: data.booking_conditions || '',
          deposit_amount: data.deposit_amount || '',
          final_payment_days: data.final_payment_days || '',
          visa_info: data.visa_info || '',
          daily_program: data.daily_program || [],
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          meta_keywords: data.meta_keywords || ''
        });
      }
    } catch (error) {
      console.error('Error fetching cruise:', error);
      alert('Ошибка загрузки круиза: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCruise();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !id) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-zа-яё0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 60);
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, id]);

  useEffect(() => {
    // Auto-fill deadline_date with departure_date when departure_date changes
    if (formData.departure_date && !id) {
      setFormData(prev => ({
        ...prev,
        deadline_date: prev.departure_date
      }));
    }
  }, [formData.departure_date, id]);

  const uploadImage = async (file) => {
    if (!file) throw new Error('Файл не выбран');
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Файл слишком большой. Максимальный размер 5MB');
    }
    if (!file.type.startsWith('image/')) {
      throw new Error('Пожалуйста, выберите изображение');
    }

    const cleanFileName = file.name.replace(/[^a-zA-Z0-9а-яА-Я._-]/g, '_');
    const fileName = `${Date.now()}_${cleanFileName}`;
    const filePath = `cruise-images/${fileName}`;

    const { data, error } = await supabase.storage
      .from('tour-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('tour-images')
      .getPublicUrl(data.path);

    return publicUrl;
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      setFormData(prev => ({ ...prev, [field]: imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Ошибка загрузки изображения: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        uploadedUrls.push(imageUrl);
      }
      setFormData(prev => ({
        ...prev,
        gallery_images: [...prev.gallery_images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      alert('Ошибка загрузки изображений: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Cabin Categories Handlers
  const updateCabinCategory = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.cabin_categories];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, cabin_categories: updated };
    });
  };

  // Ports Schedule Handlers
  const addPort = () => {
    setFormData(prev => ({
      ...prev,
      ports_schedule: [
        ...prev.ports_schedule,
        {
          port_number: prev.ports_schedule.length + 1,
          port_name: '',
          date: '',
          arrival: '-',
          stay: '-',
          departure: '',
          notes: ''
        }
      ]
    }));
  };

  const removePort = (index) => {
    setFormData(prev => {
      const updated = prev.ports_schedule.filter((_, i) => i !== index);
      return {
        ...prev,
        ports_schedule: updated.map((port, i) => ({ ...port, port_number: i + 1 }))
      };
    });
  };

  const updatePort = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.ports_schedule];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ports_schedule: updated };
    });
  };

  // Dynamic List Handlers (included, excluded, highlights)
  const addListItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeListItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateListItem = (field, index, value) => {
    setFormData(prev => {
      const updated = [...prev[field]];
      updated[index] = value;
      return { ...prev, [field]: updated };
    });
  };

  // Daily Program Handlers
  const addDay = () => {
    setFormData(prev => ({
      ...prev,
      daily_program: [
        ...prev.daily_program,
        {
          day_number: prev.daily_program.length + 1,
          day_title: '',
          day_date: '',
          day_description: ''
        }
      ]
    }));
  };

  const removeDay = (index) => {
    setFormData(prev => {
      const updated = prev.daily_program.filter((_, i) => i !== index);
      return {
        ...prev,
        daily_program: updated.map((day, i) => ({ ...day, day_number: i + 1 }))
      };
    });
  };

  const updateDay = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.daily_program];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, daily_program: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean empty list items
      const dataToSave = {
        ...formData,
        included: formData.included.filter(item => item.trim()),
        excluded: formData.excluded.filter(item => item.trim()),
        highlights: formData.highlights.filter(item => item.trim()),
        cabin_categories: formData.cabin_categories.filter(cat => cat.type),
        price_from: formData.price_from ? parseFloat(formData.price_from) : null,
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        duration_nights: formData.duration_nights ? parseInt(formData.duration_nights) : null,
        final_payment_days: formData.final_payment_days ? parseInt(formData.final_payment_days) : null
      };

      let result;
      if (id) {
        result = await supabase
          .from('cruises')
          .update(dataToSave)
          .eq('id', id);
      } else {
        result = await supabase
          .from('cruises')
          .insert([dataToSave]);
      }

      if (result.error) throw result.error;

      alert(id ? 'Круиз успешно обновлен!' : 'Круиз успешно создан!');
      navigate('/admin/cruises');
    } catch (error) {
      console.error('Error saving cruise:', error);
      alert('Ошибка сохранения: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Загрузка...</div>;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'basic':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Основная информация</h2>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Название круиза *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="8 н Легенды Восточного Моря с перелетом"
                required
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Подзаголовок
              </label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                placeholder="КРУИЗ 8н на SPECTRUM OF THE SEAS 5* (05.06.2026)"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Slug (URL) *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                placeholder="8n-legends-eastern-sea"
                required
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Автоматически генерируется из названия. Будет использоваться в URL
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Статус
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              >
                <option value="active">Активный</option>
                <option value="archived">В архиве</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Главное изображение
              </label>
              {formData.image_url && (
                <div style={{ marginBottom: '1rem' }}>
                  <img src={formData.image_url} alt="Cruise" style={{ maxWidth: '300px', borderRadius: '0.5rem' }} />
                  <button
                    type="button"
                    onClick={() => handleInputChange('image_url', '')}
                    style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    Удалить
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'image_url')}
                disabled={uploading}
                style={{ display: 'block' }}
              />
              {uploading && <p style={{ color: '#3b82f6' }}>Загрузка...</p>}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Галерея изображений
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                {formData.gallery_images.map((img, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img src={img} alt={`Gallery ${index + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '0.375rem' }} />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      style={{ position: 'absolute', top: '5px', right: '5px', width: '24px', height: '24px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryUpload}
                disabled={uploading}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                + Добавить изображения
              </button>
            </div>
          </div>
        );

      case 'dates':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Даты и длительность</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Дата отправления *
                </label>
                <input
                  type="date"
                  value={formData.departure_date}
                  onChange={(e) => handleInputChange('departure_date', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Дата окончания бронирования *
                </label>
                <input
                  type="date"
                  value={formData.deadline_date}
                  onChange={(e) => handleInputChange('deadline_date', e.target.value)}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  По умолчанию совпадает с датой отправления. Круиз будет архивирован после этой даты.
                </p>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Длительность (дни) *
                </label>
                <input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => handleInputChange('duration_days', e.target.value)}
                  placeholder="9"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Длительность (ночи) *
                </label>
                <input
                  type="number"
                  value={formData.duration_nights}
                  onChange={(e) => handleInputChange('duration_nights', e.target.value)}
                  placeholder="8"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>
            </div>
          </div>
        );

      case 'cruise':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Информация о круизе</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Название корабля *
                </label>
                <input
                  type="text"
                  value={formData.ship_name}
                  onChange={(e) => handleInputChange('ship_name', e.target.value)}
                  placeholder="Spectrum Of The Seas 5*"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Компания *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Royal Caribbean International"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Цена от (₽) *
                </label>
                <input
                  type="number"
                  value={formData.price_from}
                  onChange={(e) => handleInputChange('price_from', e.target.value)}
                  placeholder="188243"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Маршрут *
              </label>
              <input
                type="text"
                value={formData.route}
                onChange={(e) => handleInputChange('route', e.target.value)}
                placeholder="Москва - Шанхай - Шанхай - Чеджу - Пусан - Шанхай - Шанхай - Москва"
                required
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
          </div>
        );

      case 'cabins':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Категории cabin</h2>
            <p style={{ color: '#6b7280' }}>Укажите цены для каждого типа кабины</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {formData.cabin_categories.map((cabin, index) => (
                <div key={index} style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'capitalize' }}>
                    {cabin.type}
                  </label>
                  <input
                    type="number"
                    value={cabin.price}
                    onChange={(e) => updateCabinCategory(index, 'price', e.target.value)}
                    placeholder="Цена"
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'ports':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Расписание портов</h2>
              <button
                type="button"
                onClick={addPort}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                + Добавить порт
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>#</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Порт</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Дата</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Прибытие</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Стоянка</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Убытие</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Заметки</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.ports_schedule.map((port, index) => (
                    <tr key={index}>
                      <td style={{ padding: '0.5rem' }}>{port.port_number}</td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={port.port_name}
                          onChange={(e) => updatePort(index, 'port_name', e.target.value)}
                          placeholder="Москва"
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="date"
                          value={port.date}
                          onChange={(e) => updatePort(index, 'date', e.target.value)}
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={port.arrival}
                          onChange={(e) => updatePort(index, 'arrival', e.target.value)}
                          placeholder="19:00"
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={port.stay}
                          onChange={(e) => updatePort(index, 'stay', e.target.value)}
                          placeholder="24-00"
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={port.departure}
                          onChange={(e) => updatePort(index, 'departure', e.target.value)}
                          placeholder="19:00"
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={port.notes}
                          onChange={(e) => updatePort(index, 'notes', e.target.value)}
                          placeholder=""
                          style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => removePort(index)}
                          disabled={formData.ports_schedule.length <= 1}
                          style={{ padding: '0.25rem 0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: formData.ports_schedule.length <= 1 ? 'not-allowed' : 'pointer' }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'description':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Описание и включено/не включено</h2>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Полное описание
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Подробное описание круиза..."
                rows={5}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '500' }}>Что включено</label>
                <button
                  type="button"
                  onClick={() => addListItem('included')}
                  style={{ padding: '0.25rem 0.75rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  + Добавить
                </button>
              </div>
              {formData.included.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateListItem('included', index, e.target.value)}
                    placeholder="Авиаперелет China Eastern..."
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('included', index)}
                    disabled={formData.included.length <= 1}
                    style={{ padding: '0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: formData.included.length <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '500' }}>Что не включено</label>
                <button
                  type="button"
                  onClick={() => addListItem('excluded')}
                  style={{ padding: '0.25rem 0.75rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  + Добавить
                </button>
              </div>
              {formData.excluded.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateListItem('excluded', index, e.target.value)}
                    placeholder="Экскурсия в Чеджу и Пусан..."
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('excluded', index)}
                    disabled={formData.excluded.length <= 1}
                    style={{ padding: '0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: formData.excluded.length <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '500' }}>Особенности круиза (highlights)</label>
                <button
                  type="button"
                  onClick={() => addListItem('highlights')}
                  style={{ padding: '0.25rem 0.75rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  + Добавить
                </button>
              </div>
              {formData.highlights.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateListItem('highlights', index, e.target.value)}
                    placeholder="Русскоговорящий представитель на борту..."
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('highlights', index)}
                    disabled={formData.highlights.length <= 1}
                    style={{ padding: '0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: formData.highlights.length <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Комментарий менеджера
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => handleInputChange('comment', e.target.value)}
                placeholder="Этот вариант на самые праздники. Мы выбрали его, потому что..."
                rows={5}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
            </div>
          </div>
        );

      case 'flight':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Информация о перелете</h2>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Общая информация о перелете
              </label>
              <textarea
                value={formData.flight_info}
                onChange={(e) => handleInputChange('flight_info', e.target.value)}
                placeholder="Перелет идет по закрытому туроператорскому тарифу..."
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Рейс туда
              </label>
              <input
                type="text"
                value={formData.flight_departure}
                onChange={(e) => handleInputChange('flight_departure', e.target.value)}
                placeholder="MU592 05 июня Москва 19:05-08:55 Шанхай"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Рейс обратно
              </label>
              <input
                type="text"
                value={formData.flight_return}
                onChange={(e) => handleInputChange('flight_return', e.target.value)}
                placeholder="MU247 13 июня Шанхай 09:25-Москва 14:25"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
          </div>
        );

      case 'booking':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Условия бронирования</h2>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Условия бронирования
              </label>
              <textarea
                value={formData.booking_conditions}
                onChange={(e) => handleInputChange('booking_conditions', e.target.value)}
                placeholder="Депозит 300 USD на человека, невозвратный..."
                rows={4}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Сумма депозита
                </label>
                <input
                  type="text"
                  value={formData.deposit_amount}
                  onChange={(e) => handleInputChange('deposit_amount', e.target.value)}
                  placeholder="300 USD"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Финальный платеж за (дней)
                </label>
                <input
                  type="number"
                  value={formData.final_payment_days}
                  onChange={(e) => handleInputChange('final_payment_days', e.target.value)}
                  placeholder="70"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Визовая информация
              </label>
              <textarea
                value={formData.visa_info}
                onChange={(e) => handleInputChange('visa_info', e.target.value)}
                placeholder="Виза в Южную Корею НЕ нужна, даже КЕТА..."
                rows={4}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
            </div>
          </div>
        );

      case 'program':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Программа по дням</h2>
              <button
                type="button"
                onClick={addDay}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                + Добавить день
              </button>
            </div>

            {formData.daily_program.map((day, index) => (
              <div key={index} style={{ padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>День {day.day_number}</h3>
                  <button
                    type="button"
                    onClick={() => removeDay(index)}
                    disabled={formData.daily_program.length <= 1}
                    style={{ padding: '0.25rem 0.75rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: formData.daily_program.length <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Удалить день
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Заголовок дня
                    </label>
                    <input
                      type="text"
                      value={day.day_title}
                      onChange={(e) => updateDay(index, 'day_title', e.target.value)}
                      placeholder="Вылет в Шанхай"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Дата
                    </label>
                    <input
                      type="date"
                      value={day.day_date}
                      onChange={(e) => updateDay(index, 'day_date', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Описание дня
                  </label>
                  <textarea
                    value={day.day_description}
                    onChange={(e) => updateDay(index, 'day_description', e.target.value)}
                    placeholder="Подробное описание программы на этот день..."
                    rows={6}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case 'seo':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>SEO оптимизация</h2>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Meta Title
              </label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleInputChange('meta_title', e.target.value)}
                placeholder="Круиз 8н Легенды Восточного Моря | Spectrum Of The Seas"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Рекомендуемая длина: 50-60 символов
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Meta Description
              </label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => handleInputChange('meta_description', e.target.value)}
                placeholder="Круиз на Spectrum Of The Seas 5* с перелетом из Москвы. Маршрут: Москва - Шанхай - Чеджу - Пусан. Цена от 188243 ₽"
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Рекомендуемая длина: 150-160 символов
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Meta Keywords
              </label>
              <input
                type="text"
                value={formData.meta_keywords}
                onChange={(e) => handleInputChange('meta_keywords', e.target.value)}
                placeholder="круиз, китай, море, отдых, путешествие"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Ключевые слова через запятую
              </p>
            </div>
          </div>
        );

      default:
        return <div>Section under construction</div>;
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
          {id ? 'Редактировать круиз' : 'Добавить круиз'}
        </h1>
        <button
          onClick={() => navigate('/admin/cruises')}
          style={{ padding: '0.5rem 1rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
        >
          ← Назад к списку
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '2rem' }}>
          {/* Sidebar Navigation */}
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content', position: 'sticky', top: '1rem' }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {sections.map(section => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: activeSection === section.id ? '#3b82f6' : 'transparent',
                    color: activeSection === section.id ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: activeSection === section.id ? 'bold' : 'normal',
                    transition: 'all 0.2s'
                  }}
                >
                  {section.icon} {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {renderSection()}

            {/* Save Button */}
            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                {loading ? 'Сохранение...' : 'Сохранить круиз'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/cruises')}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CruiseForm;
